import {
  Dimensions,
  Gutter,
  Parapet,
  Roof,
  Wall,
  Configuration,
  WoodWall,
  ElementWall,
  SlidingGlassWallOptions,
  OptionsMaterial,
  Params,
  Spy,
  SunScreen,
  Lights,
  RoofGlassMat,
} from "src/types/configuration";
import { UiWall } from "src/components/Tabs/WallsTab/components/WallSelector";
// import ConfiguratorService from "src/services/configuratorService";
import { getWallPosition } from "src/helpers/walls";
import CalculationService from "src/services/calculationService";
// @ts-ignore
import cloneDeep from "lodash/cloneDeep";
// import { useConfiguration } from "src/contexts/ConfigurationContext";
import {
  guuters,
  roofTypes,
} from "src/components/Tabs/RoofTypeTab/RoofTypeTab";
import { structureColor } from "src/components/Tabs/DimensionsTab/DimensionsTab";

class ConfigurationService {
  private static instance: ConfigurationService;
  private calculationService?: CalculationService;
  private roofType?: string;
  private dimensions?: Dimensions;
  private optionsMaterial?: OptionsMaterial;
  private params?: Params;
  private parapet?: Parapet;
  private roof?: Roof;
  private gutter?: Gutter;
  private walls?: Wall[];
  private storages: { left: boolean; right: boolean } = {
    left: false,
    right: false,
  };
  private spy?: Spy;
  private sunScreen?: SunScreen;
  private lights?: Lights;
  private roofGlassMat?: RoofGlassMat;
  private roofMaterial?: string;

  constructor(
    configuration: Configuration,
    calculationService: CalculationService
  ) {
    if (ConfigurationService.instance) {
      return ConfigurationService.instance;
    }

    this.calculationService = calculationService;
    this.roofType = configuration.roofType;
    this.dimensions = configuration.dimensions;
    // this.parapet = configuration.parapet;
    this.gutter = configuration.gutter;
    this.roof = configuration.roof;
    this.walls = configuration.walls;
    // this.storages = configuration.storages;
    this.optionsMaterial = configuration.optionsMaterial;
    this.params = configuration.params;
    this.spy = configuration.spy;
    this.sunScreen = configuration.sunScreen;
    this.lights = configuration.lights;
    this.roofGlassMat = configuration.roofGlassMat;
    this.roofMaterial = configuration.roofMaterial;

    ConfigurationService.instance = this;
  }

  public getRoofType(): string {
    if (!this.roofType) {
      throw new Error("RoofType is not set yet.");
    }

    return this.roofType;
  }

  public getDimensions(): Dimensions {
    if (!this.dimensions) {
      throw new Error("Dimensions are not set yet.");
    }

    return this.dimensions as Dimensions;
  }

  public getParapet(): Parapet {
    if (!this.parapet) {
      throw new Error("Parapet is not set yet.");
    }

    return this.parapet as Parapet;
  }

  public getGutter(): Gutter {
    if (!this.gutter) {
      throw new Error("Gutter is not set yet.");
    }

    return this.gutter as Gutter;
  }

  public getRoof(): Roof {
    if (!this.roof) {
      throw new Error("Roof is not set yet.");
    }

    return this.roof as Roof;
  }

  public getWalls(): Wall[] {
    return this.walls ?? [];
  }

  public getStorages() {
    return this.storages;
  }

  public getSpy() {
    return this.spy as Spy;
  }
  public getSunScreen() {
    return this.sunScreen as SunScreen;
  }

  public getLights() {
    return this.lights;
  }

  public getRoofGlassMat() {
    return this.roofGlassMat;
  }

  public getRoofMaterial() {
    return this.roofMaterial;
  }

  public getDefaultWoodWall(): WoodWall {
    return {
      side: "left",
      innerColor: "empty",
      outerColor: "empty",
      rotate: false,
      window: "empty",
      door: "empty",
      doorSide: "right",
    };
  }

  getDefaultSlidingGlassWallOptions(): SlidingGlassWallOptions {
    return {
      doorBrushes: 0,
      stainlessSteelHandle: 0,
      lockProfile: 0,
      schuifrichting: "empty",
      contributors: 0,
      glasbok: 0,
      glassColor: "empty",
    };
  }

  public getDefaultElementWall(): ElementWall {
    return {
      side: "left",
      innerColor: "empty",
      outerColor: "empty",
      elements: [],
    };
  }

  public getDefaultWall(
    wallId: string,
    inner: boolean = false,
    isRightSide: boolean = false
  ): Wall {
    return {
      id: wallId,
      position: getWallPosition(wallId),
      inner: inner,
      isRightSide: isRightSide,
      storage: "empty",
      slidingGlassWall: "empty",
      slidingGlassWallOptions: {
        doorBrushes: 0,
        stainlessSteelHandle: 0,
        lockProfile: 0,
        schuifrichting: "empty",
        contributors: 0,
        glasbok: 0,
        glassColor: "empty",
      },
      woodWall: this.getDefaultWoodWall(),
      elementWall: this.getDefaultElementWall(),
      glassDark: false,
      lessTracks: false,
      orientation: "left", // left, middle, right
    };
  }

  public setState(state: Configuration): void {
    this.roofType = cloneDeep(state.roofType);
    this.dimensions = cloneDeep(state.dimensions);
    // this.parapet = cloneDeep(state.parapet);
    this.gutter = cloneDeep(state.gutter);
    this.roof = cloneDeep(state.roof);
    this.walls = cloneDeep(state.walls);
    // this.storages = cloneDeep(state.storages);
    this.optionsMaterial = cloneDeep(state.optionsMaterial);
    this.params = cloneDeep(state.params);
    this.spy = cloneDeep(state.spy);
    this.sunScreen = cloneDeep(state.sunScreen);
    this.lights = cloneDeep(state.lights);
    this.roofGlassMat = cloneDeep(state.roofGlassMat);
    this.roofMaterial = cloneDeep(state.roofMaterial);
  }

  public setRoofType(roofType: string): void {
    this.roofType = roofType;
  }

  public setDimensions(dimensions: Dimensions): void {
    this.dimensions = dimensions;
  }

  public setOptionsMaterial(optionsMaterial: OptionsMaterial): void {
    this.optionsMaterial = optionsMaterial;
  }

  public setParapet(parapet: Parapet): void {
    this.parapet = parapet;
  }

  public setRoof(roof: Roof): void {
    this.roof = roof;
  }

  public setGutter(gutter: Gutter): void {
    this.gutter = gutter;
  }

  public setWalls(walls: Wall[]): void {
    this.walls = walls;
  }

  public setStorages(storages: { left: boolean; right: boolean }): void {
    this.storages = storages;
  }

  public getOptionsMaterial() {
    return this.optionsMaterial;
  }

  public findWall(wallId: string, inner: boolean = false): Wall {
    const foundWall = this.getWalls().find((wall) => wall.id === wallId);

    return foundWall ? { ...foundWall } : this.getDefaultWall(wallId, inner);
  }

  public findWallbyUiWall(selectedWall: UiWall | null) {
    if (!selectedWall) {
      return this.getDefaultWall("");
    }

    const foundWall = this.getWalls().find(
      (wall) => wall.id === selectedWall.id
    );

    return foundWall
      ? { ...foundWall }
      : this.getDefaultWall(selectedWall.id, selectedWall.inner);
  }

  public getConfiguration() {
    return {
      roofType: this.roofType,
      dimensions: this.dimensions,
      roof: this.roof,
      parapet: this.parapet,
      gutter: this.gutter,
      walls: this.walls,
      storages: this.storages,
      optionsMaterial: this.optionsMaterial,
      params: this.params,
      spy: this.spy,
      sunScreen: this.sunScreen,
      lights: this.lights,
      roofGlassMat: this.roofGlassMat,
      roofMaterial: this.roofMaterial,
      total_price: this.calculationService?.calculateTotalPrice() ?? 0,
    };
  }

  private getGooter() {
    // const guttertype = this.gutter?.type;
    // if (guttertype === "default" || !guttertype) return "Recht";
    // return (
    //   guttertype.charAt(0).toUpperCase() + guttertype.slice(1).toLowerCase()
    // );

    const gutter = guuters.find((i) => i.value === this.gutter?.type);
    if (!gutter) return gutter;

    // RoofTypeTab used to write the absolute price into the shared `guuters`
    // array while rendering, so this payload carried a price only when the
    // customer had opened that tab, and a surcharge once the tab switched to
    // relative prices. Resolve it here instead, so the offer always receives
    // the same absolute article price regardless of what was on screen.
    return {
      ...gutter,
      price: this.calculationService?.getGutter(gutter.value) ?? 0,
    };
  }

  private getKleur() {
    const value = this.roofMaterial;
    if (!value) return "";

    // if (value === "mat_white") return "Wit, RAL 9010";
    // else if (value === "mat_blue") return "Antraciet, RAL 7016";
    // else if (value === "mat_brown") return "QuartzGrey, RAL 7039";
    // else if (value === "mat_black") return "JetBlack, RAL 9015";
    // else return "";

    return structureColor.find((i) => i.value === value)?.name;
  }

  private getGlass() {
    const value = this.roofGlassMat;
    if (!value) return "";

    const type = value.glass;

    if (type === "glass") {
      if (value.glassshade === "light") return "Glas, Helder";
      else if (value.glassshade === "melk") return "Glas, Melkglas";
      else return "Glas, Zonwerend";
    } else if (type === "poly") {
      const shade = value.polyshade;
      if (shade === "light") return "Polycarbonaat, Opaal";
      else if (shade === "dark") return "Polycarbonaat, Zonwerend";
      else return "Polycarbonaat, Helder ";
    } else return "Polycarbonaat, Breed";
  }

  private getSpie() {
    const value = this.spy;
    if (!value) return "";

    let str = "";

    if (value.left && value.right) str = "Links & rechts";
    else if (value.left) str = "Links";
    else if (value.right) str = "Rechts";
    else return "";

    return `${str}, ${this.getGlass()}`;
  }

  private getScreen() {
    const value = this.sunScreen;
    if (!value || !value.enabled) return "";

    let str = "";

    if (!value.top) str = `Onder het dak`;
    else str = `Bovenop het dak`;

    return str;
  }
  private getPoer() {
    const value = this.optionsMaterial?.poer;
    if (!value) return "";

    let str = "";

    if (value === "poer") str = "Poer";
    else if (value === "hwapoer") str = "HWA Poer";

    return str;
  }

  private getFundering() {
    const value = this.optionsMaterial?.fundering;
    if (!value) return "";

    if (value === "fundatiekoker" || value === "fundatiekokerpoer")
      return value === "fundatiekoker"
        ? "Fundatiekoker"
        : "Fundatiekoker en poer";

    return "";
  }

  private getLightBulbs() {
    const value = this.lights;
    if (!value || !value.enabled) return "";

    if (value.everyLigger) return "In elke dakligger";
    else return "In elke 2e dakligger";
  }

  private getGlassWalls() {
    const truewalls = window.CONF.house.walls.map((item) => {
      return [
        item.sections[0].type,
        item.sections[0].customization.includes("glassSectionDark"),
      ];
    });
    // let dark = 0;
    // let light = 0;
    let arr: string[] = [];
    truewalls.map((i) => {
      if (i[0].startsWith("window") || i[0].startsWith("door")) {
        const parts = i[0].split("_");
        const numberValue = parts[1] ? parseInt(parts[1][0]) : 0;
        // i[1] ? (dark += numberValue) : (light += numberValue);

        const str = i[0].startsWith("door")
          ? `Glazen schuifwand ${numberValue}-delig, ${
              i[1] ? "Zonwerend" : "Helder"
            }`
          : `Glazen schuifwand steellook ${numberValue}-delig, ${
              i[1] ? "Zonwerend" : "Helder"
            }`;

        arr.push(str);
      }

      if (i[0].startsWith("alum")) {
        arr.push("Aluminium Zijwand");
      }
    });

    return arr;
  }

  // public getComponents() {
  //   // const { state } = useConfiguration();
  //   const width = this.dimensions?.width ?? 0;
  //   const depth = this.dimensions?.depth ?? 0;
  //   const height = this.dimensions?.height ?? 0;

  //   const components = [
  //     { label: "Type", value: this.roofType },
  //     { label: "Breedte", value: width },
  //     { label: "Diepte", value: depth },
  //     { label: "Hoogte", value: height },
  //     { label: "Gutter types", value: this.getGooter() },
  //     { label: "Kleur", value: this.getKleur() },
  //     { label: "Dakmateriaal", value: this.getGlass() },
  //     { label: "Glazen schuifwand", value: "fix me later" },
  //     { label: "Spie optie", value: this.getSpie() },
  //     { label: "Zonwering", value: this.getScreen() },
  //     { label: "Poer", value: this.getPoer() },
  //     { label: "Opties", value: this.getFundering() },
  //   ];

  //   // components.push();

  //   return components;
  // }

  public getConfigurationHash() {
    return btoa(JSON.stringify(this.getConfiguration()));
  }

  public getSpecifications() {
    const width = this.dimensions?.width ?? 0;
    const depth = this.dimensions?.depth ?? 0;
    const height = this.dimensions?.height ?? 0;

    const components = [
      {
        label: "Type",
        value: roofTypes.find((i) => i.value === this.roofType)?.name,
      },
      { label: "Breedte", value: width },
      { label: "Diepte", value: depth },
      { label: "Hoogte", value: height },
      { label: "Goot-type", value: this.getGooter() },
      { label: "Kleur", value: this.getKleur() },
      { label: "Dakmateriaal", value: this.getGlass() },
      { label: "Wanden", value: this.getGlassWalls() },
      { label: "Spie optie", value: this.getSpie() },
      { label: "Zonwering", value: this.getScreen() },
      { label: "Verlichting", value: this.getLightBulbs() },
      { label: "Poer", value: this.getPoer() },
      { label: "Opties", value: this.getFundering() },
    ];

    // components.push();

    return components;
  }
}

export default ConfigurationService;
